// DeckTabBar.jsx
//
// Browser-tab style header for switching between the user's decks.
// Renders one chip per deck (icon, name, overflow menu when active, close),
// an active highlight, and an inline "+" button that creates a
// new deck. Tabs support:
//
//   - Click          → switch active deck
//   - Pencil (active tab) → rename   ·   Double-click tab → rename
//   - X button       → delete (with confirm)
//   - Drag           → reorder (web only)
//
// Caps respected from `state.limits` (max 12 decks). The component is
// pure-zustand-driven; the parent doesn't need to wire props.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, ScrollView, StyleSheet, Pressable, Platform, TextInput } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useDeckStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { Tooltip } from '@/components/Tooltip';

const TAB_HEIGHT = 53;
const DECK_UI_BLUE = '#3b82f6';
/** Primary actions — matches Tailwind blue-500 */
const NEW_DECK_BLUE = 'rgb(59, 130, 246)';
const NEW_DECK_BLUE_HOVER = 'rgb(37, 99, 235)';

export default function DeckTabBar() {
  const { colors } = useTheme();
  const router = useRouter();

  const decks = useDeckStore(state => state.decks);
  const activeSlug = useDeckStore(state => state.activeDeckSlug);
  const limits = useDeckStore(state => state.limits);
  const { setActiveDeckSlug, createDeck, renameDeck, deleteDeck, reorderDecks } = useDeckStore(state => state.actions);

  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  const [renamingSlug, setRenamingSlug] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [dragging, setDragging] = useState({ slug: null, dropIndex: -1 });
  const tabRefs = useRef({});
  const dragCleanupRef = useRef(null);
  const creatingRef = useRef(false);
  const [isCreating, setIsCreating] = useState(false);
  useEffect(() => () => { dragCleanupRef.current?.(); }, []);

  const canAddDeck = decks.length < (limits?.maxDecks || 12);

  // ── Switching tabs ────────────────────────────────────────────────────
  const handleSelect = useCallback((slug) => {
    if (slug === activeSlug) return;
    setActiveDeckSlug(slug);
    // Mirror the active deck into the URL so refresh / share works.
    if (Platform.OS === 'web') {
      try {
        router.setParams({ d: slug });
      } catch (_) { /* router not ready, no-op */ }
    }
  }, [activeSlug, setActiveDeckSlug, router]);

  // ── Rename ────────────────────────────────────────────────────────────
  const startRename = useCallback((deck) => {
    setRenamingSlug(deck.slug);
    setRenameValue(deck.name || '');
  }, []);

  const commitRename = useCallback(() => {
    if (!renamingSlug) return;
    const trimmed = renameValue.trim();
    const original = decks.find(d => d.slug === renamingSlug);
    if (trimmed && original && trimmed !== original.name) {
      renameDeck(renamingSlug, trimmed);
    }
    setRenamingSlug(null);
    setRenameValue('');
  }, [renamingSlug, renameValue, decks, renameDeck]);

  const cancelRename = useCallback(() => {
    setRenamingSlug(null);
    setRenameValue('');
  }, []);

  // ── Delete ────────────────────────────────────────────────────────────
  const handleDelete = useCallback((deck) => {
    showConfirmationModal({
      title: 'Delete deck?',
      message: `"${deck.name}" and its column layout will be removed. Existing conversations are not deleted.`,
      itemName: deck.name,
      confirmText: 'Delete deck',
      cancelText: 'Cancel',
      isDangerous: true,
      onConfirm: () => {
        deleteDeck(deck.slug);
      },
    });
  }, [deleteDeck, showConfirmationModal]);

  // ── New deck ──────────────────────────────────────────────────────────
  const handleAddDeck = useCallback(async () => {
    if (!canAddDeck || creatingRef.current) return;
    creatingRef.current = true;
    setIsCreating(true);
    const slug = await createDeck({ name: 'Untitled deck' });
    creatingRef.current = false;
    setIsCreating(false);
    if (!slug) return;
    if (Platform.OS === 'web') {
      try {
        router.setParams({ d: slug });
      } catch (_) {
        /* router not ready */
      }
    }
  }, [canAddDeck, createDeck, router]);

  // ── Drag-to-reorder (web only, mouse-based) ───────────────────────────
  const handleTabDragStart = useCallback((slug, e) => {
    if (Platform.OS !== 'web') return;
    if (renamingSlug) return;
    dragCleanupRef.current?.();

    const startX = e.clientX;
    let hasMoved = false;

    const fromIndex = decks.findIndex(d => d.slug === slug);
    if (fromIndex === -1) return;

    const computeDropIndex = (mouseX) => {
      for (let i = 0; i < decks.length; i++) {
        const el = tabRefs.current[decks[i].slug];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const midX = rect.left + rect.width / 2;
        if (mouseX < midX) return i;
      }
      return decks.length;
    };

    let latestDropIndex = -1;

    const onMove = (ev) => {
      if (!hasMoved && Math.abs(ev.clientX - startX) < 6) return;
      hasMoved = true;
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      const di = computeDropIndex(ev.clientX);
      latestDropIndex = di;
      setDragging({ slug, dropIndex: di });
    };

    const cleanup = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      dragCleanupRef.current = null;
    };
    const onUp = () => {
      cleanup();

      if (hasMoved && latestDropIndex !== -1) {
        let toIndex = latestDropIndex;
        if (toIndex > fromIndex) toIndex -= 1;
        if (toIndex !== fromIndex) reorderDecks(fromIndex, toIndex);
      }
      setDragging({ slug: null, dropIndex: -1 });
    };

    dragCleanupRef.current = cleanup;
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [decks, renamingSlug, reorderDecks]);

  // ── Render ────────────────────────────────────────────────────────────
  const isDragging = dragging.slug !== null;
  const dragFromIndex = isDragging ? decks.findIndex(d => d.slug === dragging.slug) : -1;

  if (!decks.length) return null;

  return (
    <View
      style={[
        styles.bar,
        {
          backgroundColor: colors.solid_color_block,
          borderBottomColor: colors.border_color_secondary,
        },
      ]}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.tabScroll}
        contentContainerStyle={styles.scroll}
      >
        {decks.map((deck, index) => {
          const active = deck.slug === activeSlug;
          const beingDragged = dragging.slug === deck.slug;
          const showDropBefore =
            isDragging
            && dragging.dropIndex === index
            && dragFromIndex !== index
            && dragFromIndex !== index - 1;
          const showDropAfter =
            isDragging
            && dragging.dropIndex === decks.length
            && index === decks.length - 1
            && dragFromIndex !== index;

          const isRenamingThis = renamingSlug === deck.slug;

          return (
            <React.Fragment key={deck.slug}>
              {showDropBefore && <View style={[styles.dropIndicator, { backgroundColor: DECK_UI_BLUE }]} />}
              <Pressable
                ref={(node) => {
                  if (node) tabRefs.current[deck.slug] = node;
                  else delete tabRefs.current[deck.slug];
                }}
                onPress={() => handleSelect(deck.slug)}
                onLongPress={() => startRename(deck)}
                {...(Platform.OS === 'web'
                  ? {
                      onMouseDown: (e) => {
                        // Avoid hijacking right-clicks and modifier-clicks.
                        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                        if (isRenamingThis) return;
                        handleTabDragStart(deck.slug, e);
                      },
                      onDoubleClick: () => startRename(deck),
                    }
                  : {})}
                style={({ hovered }) => [
                  styles.tab,
                  {
                    backgroundColor: active
                      ? colors.solid_color_primary
                      : hovered ? colors.solid_color_hover : 'transparent',
                    borderColor: active ? colors.border_color_secondary : 'transparent',
                    opacity: beingDragged ? 0.5 : 1,
                  },
                  active && styles.tabActive,
                ]}
              >
                <View style={[styles.iconBubble, { backgroundColor: active ? DECK_UI_BLUE : colors.text_color_tertiary }]}>
                  <Icon name={deck.icon || 'table-columns'} size={11} color={colors.solid_color_primary} solid />
                </View>

                {isRenamingThis ? (
                  <TextInput
                    autoFocus
                    value={renameValue}
                    onChangeText={setRenameValue}
                    onBlur={commitRename}
                    onSubmitEditing={commitRename}
                    onKeyPress={(e) => {
                      if (e?.nativeEvent?.key === 'Escape') cancelRename();
                    }}
                    selectTextOnFocus
                    style={[styles.renameInput, { color: colors.text_color_primary }]}
                    maxLength={60}
                  />
                ) : (
                  <Text
                    size="xs"
                    weight={active ? 'bold' : 'medium'}
                    color={active ? 'primary' : 'secondary'}
                    numberOfLines={1}
                    style={styles.tabLabel}
                  >
                    {deck.name || 'Untitled deck'}
                  </Text>
                )}

                {active && !isRenamingThis && (
                  <Tooltip label="Rename deck" side="top" disabled={Platform.OS !== 'web'}>
                    <Pressable
                      onPress={(e) => {
                        e?.stopPropagation?.();
                        startRename(deck);
                      }}
                      hitSlop={6}
                      accessibilityRole="button"
                      accessibilityLabel="Rename deck"
                      style={({ hovered }) => [
                        styles.menuBtn,
                        hovered && { backgroundColor: colors.solid_color_hover },
                      ]}
                    >
                      <Icon name="ellipsis-vertical" size={13} color={colors.text_color_secondary} solid />
                    </Pressable>
                  </Tooltip>
                )}

                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`Delete deck ${deck.name}`}
                  onPress={(e) => {
                    e?.stopPropagation?.();
                    handleDelete(deck);
                  }}
                  hitSlop={6}
                  style={({ hovered }) => [
                    styles.closeBtn,
                    hovered && { backgroundColor: colors.solid_color_hover },
                  ]}
                >
                  <Icon name="xmark" size={11} color={colors.text_color_tertiary} />
                </Pressable>
              </Pressable>
              {showDropAfter && <View style={[styles.dropIndicator, { backgroundColor: DECK_UI_BLUE }]} />}
            </React.Fragment>
          );
        })}

      </ScrollView>

      <Pressable
        onPress={handleAddDeck}
        accessibilityRole="button"
        accessibilityLabel="New deck"
        disabled={!canAddDeck || isCreating}
        style={({ hovered }) => [
          styles.addBtn,
          canAddDeck
            ? {
                backgroundColor: hovered ? NEW_DECK_BLUE_HOVER : NEW_DECK_BLUE,
                borderColor: NEW_DECK_BLUE,
                borderStyle: 'solid',
              }
            : {
                backgroundColor: colors.solid_color_tertiary,
                borderColor: colors.border_color_secondary,
                borderStyle: 'dashed',
                opacity: 0.55,
              },
        ]}
      >
        <Icon
          name="plus"
          size={13}
          color={canAddDeck ? '#ffffff' : colors.text_color_tertiary}
          solid
        />
        <Text
          size="s"
          style={{ marginLeft: 6, color: canAddDeck ? '#ffffff' : colors.text_color_tertiary }}
        >
          New deck
        </Text>
      </Pressable>

      <View style={[styles.right, { borderLeftColor: colors.border_color_secondary }]}>
        <Pressable
          onPress={() => router.push('/decks/recipes')}
          style={({ hovered }) => [
            styles.recipesBtn,
            hovered && { backgroundColor: colors.solid_color_hover },
          ]}
        >
          <Icon name="grid-2" size={13} color={colors.text_color_secondary} solid />
          <Text size="s" color="secondary" style={{ marginLeft: 6 }}>
            Recipes
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    height: TAB_HEIGHT,
    flexDirection: 'row',
    alignItems: 'stretch',
    borderBottomWidth: 1,
    flexShrink: 0,
  },
  tabScroll: {
    flex: 1,
    minWidth: 0,
  },
  scroll: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    gap: 4,
    flexGrow: 1,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 40,
    paddingHorizontal: 12,
    paddingRight: 8,
    borderRadius: 8,
    borderWidth: 1,
    gap: 10,
    maxWidth: 220,
    minWidth: 110,
    ...(Platform.OS === 'web' ? { cursor: 'grab', userSelect: 'none', transition: 'background-color 0.15s ease' } : {}),
  },
  tabActive: {
    ...(Platform.OS === 'web' ? { boxShadow: '0 1px 0 rgba(0,0,0,0.04)' } : {}),
  },
  iconBubble: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabLabel: {
    flex: 1,
    minWidth: 0,
  },
  renameInput: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    padding: 0,
    margin: 0,
    minWidth: 60,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  menuBtn: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  closeBtn: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  dropIndicator: {
    width: 2,
    height: 34,
    alignSelf: 'center',
    borderRadius: 1,
    marginHorizontal: 1,
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    height: 36,
    paddingHorizontal: 14,
    borderRadius: 6,
    borderWidth: 1,
    marginHorizontal: 6,
    flexShrink: 0,
    ...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'background-color 0.15s ease' } : {}),
  },
  right: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    borderLeftWidth: 1,
  },
  recipesBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 36,
    paddingHorizontal: 12,
    borderRadius: 6,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
});
